import { CLASS_NAMES, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the EventSignupForm module
 */
export default class EventSignupForm extends MessageBus {
  /**
   * Constructor function of the EventSignupForm class. Must receive one argument, a DOM
   * node representing the event signup form.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    /**
     * DOM node that is passed into the constructor
     * @property {object} element DOM node that is passed into the constructor
     */
    this.element = element;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {object} EventSignupForm A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();
    return this;
  }

  /**
   * Cache DOM References
   *
   * Find all necessary DOM elements used in the view and cache them
   * @returns {object} EventSignupForm A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.checkbox = this.element.querySelector(
      `.${CLASS_NAMES.EVENT_SIGNUP_CHECKBOX}`
    );
    this.requiredInputs = this.element.querySelectorAll(
      CLASS_NAMES.EVENT_SIGNUP_INPUTS
    );
    this.form = this.element.querySelector(`#${CLASS_NAMES.EVENT_SIGNUP_FORM}`);
    this.sectionHeading = this.element.querySelector(
      `.${CLASS_NAMES.EVENT_SIGNUP_SECTION_HEADING}`
    );
    this.description = this.element.querySelector(
      `.${CLASS_NAMES.EVENT_SIGNUP_DESCRIPTION}`
    );
    this.thankYouHeadline = this.element.querySelector(
      `.${CLASS_NAMES.EVENT_SIGNUP_THANK_YOU_HEADLINE}`
    );
    this.thankYouDescription = this.element.querySelector(
      `.${CLASS_NAMES.EVENT_SIGNUP_THANK_YOU_DESCRIPTION}`
    );

    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} EventSignupForm A reference to the current instance of the class
   * @chainable
   */
  enable() {
    if (this.checkbox) {
      this.checkbox.addEventListener(
        EVENTS.CLICK,
        this.toggleCheckbox.bind(this)
      );
    }

    if (this.requiredInputs) {
      this.requiredInputs.forEach((input) => {
        input.addEventListener(EVENTS.BLUR, this.validateField.bind(this));
        input.addEventListener(EVENTS.CHANGE, this.validateField.bind(this));
      });
    }

    if (this.form) {
      this.form.addEventListener(EVENTS.SUBMIT, this.handleSubmit.bind(this));
    }

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.toggleCheckbox = this.toggleCheckbox.bind(this);
    this.validateField = this.validateField.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);

    return this;
  }

  /**
   * Toggle the checkbox state
   * @returns {object} EventSignupForm A reference to the current instance of the class
   * @param {Event} event - the event object
   */
  toggleCheckbox(event) {
    const checkboxField = event.currentTarget;
    const checkbox = checkboxField.previousElementSibling;

    checkboxField.classList.toggle('checked');
    if (checkbox.checked) {
      checkbox.checked = false;
      checkbox.removeAttribute('checked');
    } else {
      checkbox.checked = true;
      checkbox.setAttribute('checked', 'checked');
    }

    return this;
  }

  /**
   * Validate the input field
   * @param {Event} event - the event object
   * @returns {boolean} isValid - whether the field is valid or not
   */
  validateField(event) {
    const field = event.currentTarget;
    const fieldValue = field.value;
    const fieldName = field.getAttribute('data-name');

    let isValid = true;

    if (field.required && fieldValue.trim() === '') {
      this.showError(
        field,
        `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`
      );
      isValid = false;
    } else if (fieldName === 'Email' && fieldValue.trim() !== '') {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(fieldValue)) {
        this.showError(field, 'Please enter a valid email address');
        isValid = false;
      } else {
        this.removeError(field);
      }
    } else {
      this.removeError(field);
    }

    return isValid;
  }

  /**
   * Show error message for the field
   * @param {HTMLElement} field - the input field
   * @param {string} message - the error message
   * @returns {object} EventSignupForm A reference to the current instance of the class
   */
  showError(field, message) {
    this.removeError(field);
    field.classList.add('invalid');
    field.insertAdjacentHTML(
      'afterend',
      `<div class="error-text">${message}</div>`
    );

    return this;
  }

  /**
   * Remove error message for the field
   * @param {HTMLElement} field - the input field
   * @returns {object} EventSignupForm A reference to the current instance of the class
   */
  removeError(field) {
    field.classList.remove('invalid');
    const errorText = field.nextElementSibling;
    if (errorText && errorText.classList.contains('error-text')) {
      errorText.remove();
    }

    return this;
  }

  /**
   * Handle form submission
   * @param {Event} event - the event object
   * @returns {object} EventSignupForm A reference to the current instance of the class
   */
  handleSubmit(event) {
    let isFormValid = true;
    this.requiredInputs.forEach((input) => {
      if (!this.validateField({ currentTarget: input })) {
        isFormValid = false;
      }
    });

    if (isFormValid) {
      this.form.style.display = 'none';
      this.sectionHeading.style.display = 'none';
      this.description.style.display = 'none';
      if (this.thankYouHeadline) {
        this.thankYouHeadline.classList.add('show');
      }
      if (this.thankYouDescription) {
        this.thankYouDescription.classList.add('show');
      }
    } else {
      event.preventDefault();
    }
    return this;
  }
}
